import { z } from "zod";

import { GoogleFreeBookingLinksPhoneType } from "@/lib/gql/__generated__/graphql";
import {
	GoogleFreeBookingLinksAddressInputSchema,
	GoogleFreeBookingLinksPhoneNumberInputSchema,
	GoogleFreeBookingLinksSettingsInputSchema,
} from "@/lib/zod/__generated__/validation-schema";

import {
	geoLocationInputSchema,
	postalCodeZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

import type { ZodError } from "zod";

export const googleFreeBookingLinksSettingsInputZodSchema = GoogleFreeBookingLinksSettingsInputSchema.extend({
	hotelName: z.string().min(1),
	webSite: z.string().url().min(1),
	address: GoogleFreeBookingLinksAddressInputSchema.extend({
		postalCode: postalCodeZodSchema,
		province: z.string().min(1),
		city: z.string().min(1),
	}),
	geoLocation: geoLocationInputSchema.superRefine((value, ctx) => {
		if (!value?.lat) {
			ctx.addIssue({
				path: ["lat"],
				code: z.ZodIssueCode.custom,
				message: "緯度を設定してください",
			});
		}

		if (!value?.lon) {
			ctx.addIssue({
				path: ["lon"],
				code: z.ZodIssueCode.custom,
				message: "経度を設定してください",
			});
		}

		return z.NEVER;
	}),
	phoneNumbers: z
		.array(
			GoogleFreeBookingLinksPhoneNumberInputSchema.extend({
				phoneNumber: z
					.string()
					.nonempty()
					.min(10, {
						message: "電話番号は10文字以上で入力してください。",
					})
					.max(13, {
						message: "電話番号は13文字以内で入力してください。",
					})
					.refine(
						(phoneNumber) => {
							// 1. 数字とハイフンのみで構成されているか
							if (!/^[0-9-]+$/.test(phoneNumber)) return false;

							// 2. ハイフンの数が 0 または 2
							const hyphenCount = (phoneNumber.match(/-/g) || []).length;
							if (hyphenCount !== 0 && hyphenCount !== 2) return false;

							// 3. 全体で同じ文字が10回以上連続しない
							const noHyphen = phoneNumber.replace(/-/g, "");
							if (/(.)\1{9}/.test(noHyphen)) return false;

							return true;
						},
						{
							message: "実際に存在する電話番号を入力してください。",
						},
					),
				phoneType: z.nativeEnum(GoogleFreeBookingLinksPhoneType, {
					invalid_type_error: "電話番号の種類を選択してください",
				}),
			}),
		)
		.max(5, {
			message: "電話番号は5つまでしか設定できません",
		}),

	defaultPlanID: z.string().nullish(),
});

export type GoogleFreeBookingLinksSettingsZodSchemaType = z.infer<typeof googleFreeBookingLinksSettingsInputZodSchema>;

export type GoogleFreeBookingLinksSettingsZodParseErrorType = ZodError<GoogleFreeBookingLinksSettingsZodSchemaType>;
